import Konva from 'konva';
import { watch } from 'vue';
import HaspObject from './HaspObject';
import { useDatasetStore } from '../stores/datasetStore.js';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { resolvePadding } from './properties/v2/padding.js';
import { chartPlotBox, shouldExportChartPaddingSide } from './chartPadding.js';
import { formatChartYAxisLabel } from './chartAxisLabels.js';

// Deterministic wave parameters for each series (phase in radians, amplitude and
// midpoint as fractions of the Y range). Up to 6 visually distinct patterns.
const WAVE_PARAMS = [
    { phase: 0,              amp: 0.32, mid: 0.50 },
    { phase: Math.PI / 2,   amp: 0.26, mid: 0.58 },
    { phase: Math.PI,        amp: 0.30, mid: 0.42 },
    { phase: Math.PI / 3,   amp: 0.22, mid: 0.62 },
    { phase: -Math.PI / 4,  amp: 0.28, mid: 0.38 },
    { phase: Math.PI * 0.7, amp: 0.20, mid: 0.54 },
];

function generateSeriesSample(n, seriesIdx, minVal, range) {
    const { phase, amp, mid } = WAVE_PARAMS[seriesIdx % WAVE_PARAMS.length];
    return Array.from({ length: n }, (_, i) => {
        const t = (i / Math.max(n - 1, 1)) * Math.PI * 4; // 2 full sine cycles
        const norm = Math.max(0, Math.min(1, mid + amp * Math.sin(t + phase)));
        return minVal + range * norm;
    });
}

export default class HaspChart extends HaspObject {

    constructor(config) {
        super(config);

        // One binding per axis — mirrors HaspGauge.js's _scaleFontBinding:
        // drawChart() recreates every label Konva.Text node from scratch on
        // each redraw, so there's no persistent node to update fontFamily on
        // in place. The binding just keeps _xAxisFontFamily/_yAxisFontFamily
        // current; only the asynchronous "Font Resource finished loading"
        // transition (loaded === true) triggers its own redraw here — the
        // synchronous/fallback stage's redraw is already covered by whatever
        // called _applyAxisFont() (see _applyAxisFont() below).
        this._xAxisFontBinding = new FontBinding((family, loaded) => {
            this._xAxisFontFamily = family;
            if (loaded) this.drawChart();
        });
        this._yAxisFontBinding = new FontBinding((family, loaded) => {
            this._yAxisFontFamily = family;
            if (loaded) this.drawChart();
        });

        this.on('transform',    () => this.drawChart());
        this.on('transformend', () => {
            this.size(this.rect.size());
            this.drawChart();
        });

        // Widget data — stays on the legacy property/update mechanism.
        for (const prop of ['series', 'point_count', 'data_min', 'data_max', 'decimals']) {
            this.on(prop, () => this.drawChart());
        }

        // Style properties (all part of 'main') — Style API.
        for (const prop of ['type', 'grid_color', 'hdiv', 'vdiv',
                             'line_width', 'point_size', 'padding',
                             'pad_top', 'pad_right', 'pad_bottom', 'pad_left',
                             'grid_dash_width', 'grid_dash_gap']) {
            this.onStyle('main', prop, () => this.drawChart());
        }

        // Axis appearance + enable toggle now live in their own part per axis
        // — see chart.js's x_axis/y_axis schema parts and shared.js's
        // AXIS_STYLE.
        const AXIS_SCALE_PROP = { x_axis: 'x_scale', y_axis: 'y_scale' };
        for (const [part, scaleProp] of Object.entries(AXIS_SCALE_PROP)) {
            for (const prop of ['text_font', 'text_color',
                                 'y_axis_decimals',
                                 'major_tick_color', 'major_tick_width', 'major_tick_size',
                                 'minor_tick_color', 'minor_tick_width', 'minor_tick_size', 'minor_tick_count',
                                 'label_gap', scaleProp]) {
                this.onStyle(part, prop, () => this.drawChart());
            }
            // font/text_font_family both feed _applyAxisFont() (resource +
            // fallback family) rather than a plain redraw — see HaspLabel.js/
            // HaspGauge.js's identically-shaped `font`/`text_font_family` pair.
            this.onStyle(part, 'font', () => this._applyAxisFont(part));
            this.onStyle(part, 'text_font_family', () => this._applyAxisFont(part));
        }

        // auto_range only affects live device rendering (recomputing min/max from
        // currently-visible series data) — the static designer preview has no
        // runtime visibility concept, so this property is exported but visually
        // inert here; no redraw wiring needed.

        this.draggable(true);
        this._applyAxisFont('x_axis');
        this._applyAxisFont('y_axis');
        this.drawChart();

        // Watch the linked dataset in the store so any change (add/remove/recolor series,
        // edit history_hours or interval) immediately redraws the chart without requiring
        // the user to have the Properties Panel open.
        this._stopDatasetWatch = watch(
            () => {
                if (!this.dataset) return null;
                const ds = useDatasetStore().getDataset(this.dataset);
                if (!ds) return null;
                return JSON.stringify(ds.series) + `:${ds.history_hours}:${ds.interval}`;
            },
            () => {
                if (!this.dataset) return;
                const ds = useDatasetStore().getDataset(this.dataset);
                if (!ds) return;
                this.series = ds.series.map(s => s.color);
                const ptCount = Math.round((ds.history_hours * 3600) / ds.interval);
                if (ptCount > 0) this.point_count = ptCount;
                this.drawChart();
                this.getLayer()?.batchDraw();
            }
        );
        this.on('destroy', () => {
            this._stopDatasetWatch?.();
            this._xAxisFontBinding.dispose();
            this._yAxisFontBinding.dispose();
        });
    }

    /**
     * The uploaded Font Resource an axis's `font` property resolves to for
     * canvas preview (see HaspLabel.js's `_fontResource()` for the full
     * wire-format semantics — same convention, just read from the axis part
     * instead of a top-level property).
     */
    _axisFontResource(part) {
        const font = this.style(part).font;
        return font ? useFontStore().fonts.find(f => f.file === font) : null;
    }

    /**
     * Resolve which font family an axis's labels should render with —
     * mirrors HaspGauge.js's _applyScaleFont(). Since drawChart() recreates
     * every label Konva.Text node from scratch on every redraw, there's no
     * per-node fontFamily to update in place; this only keeps
     * _xAxisFontFamily/_yAxisFontFamily current (read by drawChart()) and
     * lets the FontBinding decide whether an actual redraw is warranted
     * right now (see the constructor's FontBinding callbacks above).
     */
    _applyAxisFont(part) {
        const { text_font_family } = this.style(part);
        const binding = part === 'x_axis' ? this._xAxisFontBinding : this._yAxisFontBinding;
        binding.set(this._axisFontResource(part), text_font_family || 'Roboto Condensed');
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.drawChart();
    }

    drawChart() {
        if (this.chartGroup) this.chartGroup.destroy();

        const main   = this.style('main');
        const xAxis  = this.style('x_axis');
        const yAxis  = this.style('y_axis');

        const w = this.width();
        const h = this.height();
        const padding = resolvePadding(main);

        const yScaleActive = !!yAxis.y_scale;
        const xScaleActive = !!xAxis.x_scale;

        const yFontSize   = Number(yAxis.text_font) || 0;  // 0 = auto
        const yTextColor  = yAxis.text_color || '#c8c8c8';
        const yTickColor  = yAxis.major_tick_color || '#ffffff';
        const yTickWidth  = Math.max(1, Number(yAxis.major_tick_width) || 1);
        const yTickSize   = Number(yAxis.major_tick_size) || 0;  // 0 = auto (LVGL's LV_CHART_TICK_LENGTH_AUTO)
        // Minor ticks — unlabeled subdivisions between each pair of adjacent
        // major ticks (see the Y/X-axis scale rendering below). No "0 = auto"
        // sentinel here (unlike major_tick_size) — 0 simply draws a
        // zero-length (invisible) minor tick, there's no legacy auto-length
        // behavior to preserve for a property that didn't exist before.
        const yMinorTickColor = yAxis.minor_tick_color || '#ffffff';
        const yMinorTickWidth = Math.max(1, Number(yAxis.minor_tick_width) || 1);
        const yMinorTickSize  = Math.max(0, Number(yAxis.minor_tick_size) || 0);
        // Number of equal intervals per major-to-major gap — NOT the minor
        // tick count itself (see AXIS_STYLE's minor_tick_count comment).
        const yMinorTickCount = Math.max(0, Math.floor(Number(yAxis.minor_tick_count) || 0));
        // Distance from the end of the major tick to the nearest edge of its
        // label — independent per axis, feeds both label positioning and the
        // auto-pad calculation below (see AXIS_STYLE's label_gap comment).
        const yLabelGap = Math.max(0, Number(yAxis.label_gap) || 0);
        // Kept current by _applyAxisFont()/the FontBinding callback (see
        // constructor) — resolves the selected Font Resource, falling back
        // to text_font_family (or the bundled Roboto Condensed face) until
        // unless one loads.
        const yFontFamily = this._yAxisFontFamily || 'Roboto Condensed';

        const xFontSize   = Number(xAxis.text_font) || 0;  // 0 = auto
        const xTextColor  = xAxis.text_color || '#c8c8c8';
        const xTickColor  = xAxis.major_tick_color || '#ffffff';
        const xTickWidth  = Math.max(1, Number(xAxis.major_tick_width) || 1);
        const xTickSize   = Number(xAxis.major_tick_size) || 0;  // 0 = auto
        const xMinorTickColor = xAxis.minor_tick_color || '#ffffff';
        const xMinorTickWidth = Math.max(1, Number(xAxis.minor_tick_width) || 1);
        const xMinorTickSize  = Math.max(0, Number(xAxis.minor_tick_size) || 0);
        const xMinorTickCount = Math.max(0, Math.floor(Number(xAxis.minor_tick_count) || 0));
        const xLabelGap = Math.max(0, Number(xAxis.label_gap) || 0);
        const xFontFamily = this._xAxisFontFamily || 'Roboto Condensed';

        const gridColor = main.grid_color || main.border_color || '#888888';
        // LVGL only dashes a line when BOTH values are nonzero (lv_draw_line.c:
        // `dashed = dsc->dash_gap && dsc->dash_width`) — either being 0 draws solid.
        // Applies to the inner grid division lines only, matching firmware's
        // LV_CHART_PART_SERIES_BG line style — never the series-bg border rect
        // below, which LVGL always draws solid regardless (a plain rect border).
        const gridDashWidth = Math.max(0, Number(main.grid_dash_width) || 0);
        const gridDashGap   = Math.max(0, Number(main.grid_dash_gap)   || 0);
        const gridDash      = (gridDashWidth > 0 && gridDashGap > 0) ? [gridDashWidth, gridDashGap] : undefined;
        const hdiv      = Math.max(1, Number(main.hdiv)       || 3);
        const vdiv      = Math.max(1, Number(main.vdiv)       || 5);
        const lineWidth = Math.max(1, Number(main.line_width) || 2);
        const pointSize = Math.max(0, Number(main.point_size) || 0);
        const chartType = Number(main.type)     || 1;
        const minVal    = Number(this.min)      || 0;
        const maxVal    = Number(this.max)      || 100;
        const range     = maxVal - minVal       || 1;

        // Y-axis formatting is owned by the chart. Dataset series metadata is
        // intentionally not consulted here; a shared Dataset must not change
        // this chart's presentation.
        const configuredYAxisDecimals = Number(yAxis.y_axis_decimals);
        const yAxisDecimals = Number.isFinite(configuredYAxisDecimals)
            ? Math.max(0, Math.min(6, configuredYAxisDecimals))
            : 1;

        // text_font == 0 means "auto" — use 26 px (matches firmware default body font)
        const yLabelFontSize = yFontSize > 0 ? yFontSize : 26;
        const xLabelFontSize = xFontSize > 0 ? xFontSize : 26;

        // Compute auto left padding when y_scale is active (mirrors firmware formula)
        let yAxisAutoPad = 0;
        let yTickLen = 0;
        if (yScaleActive) {
            yTickLen = yTickSize > 0 ? yTickSize : Math.max(2, Math.floor(w / 15));
            const labels = [];
            for (let i = 0; i <= hdiv + 1; i++) {
                const v = maxVal - (i / (hdiv + 1)) * range;
                labels.push(formatChartYAxisLabel(v, yAxisDecimals));
            }
            const maxLabelLen   = Math.max(...labels.map(l => l.length));
            const maxLabelWidth = Math.ceil(maxLabelLen * yLabelFontSize * 0.6);
            // yLabelGap: widening it grows leftPad automatically — no manual
            // widget padding change needed to make room for it.
            const autoPad       = yTickLen + yLabelGap + maxLabelWidth + 1;
            yAxisAutoPad = autoPad;
        }

        // Compute auto bottom padding when x_scale is active (mirrors firmware formula)
        let xAxisAutoPad = 0;
        let xTickLen = 0;
        if (xScaleActive) {
            xTickLen   = xTickSize > 0 ? xTickSize : Math.max(2, Math.floor(h / 15));
            // xLabelGap: widening it grows bottomPad automatically — no
            // manual widget padding change needed to make room for it.
            xAxisAutoPad = xTickLen + xLabelGap + xLabelFontSize + 1;
        }

        const plot = chartPlotBox({
            width: w,
            height: h,
            padding,
            yAxisAutoPad,
            xAxisAutoPad,
        });
        const { left: leftPad, top: topPad, bottom: bottomPad, width: cw, height: ch } = plot;

        // Clip the group to the widget bounds so labels near the edges are clipped
        // exactly as they are in the firmware (LVGL clips to the widget mask).
        this.chartGroup = new Konva.Group({
            x: 0, y: topPad,
            listening: false,
            clipX: 0, clipY: -topPad, clipWidth: w, clipHeight: h,
        });

        // Series-bg border rect — edges come from this rect, NOT from grid lines
        // (matches LVGL: when border_width != 0 the grid loop skips the edge lines)
        this.chartGroup.add(new Konva.Rect({
            x: leftPad, y: 0, width: cw, height: ch,
            stroke: gridColor, strokeWidth: 1, fill: 'transparent',
            listening: false,
        }));

        // Horizontal grid lines — INNER only (i = 1 to hdiv, edges excluded)
        for (let i = 1; i <= hdiv; i++) {
            const y = (ch / (hdiv + 1)) * i;
            this.chartGroup.add(new Konva.Line({
                points: [leftPad, y, leftPad + cw, y],
                stroke: gridColor,
                strokeWidth: 1,
                dash: gridDash,
                listening: false,
            }));
        }

        // Vertical grid lines — INNER only (i = 1 to vdiv, edges excluded)
        for (let i = 1; i <= vdiv; i++) {
            const x = leftPad + (cw / (vdiv + 1)) * i;
            this.chartGroup.add(new Konva.Line({
                points: [x, 0, x, ch],
                stroke: gridColor,
                strokeWidth: 1,
                dash: gridDash,
                listening: false,
            }));
        }

        // Y-axis scale: tick marks + labels (only when y_scale == 1)
        if (yScaleActive) {
            for (let i = 0; i <= hdiv + 1; i++) {
                const y     = (ch / (hdiv + 1)) * i;
                const value = maxVal - (i / (hdiv + 1)) * range;
                const text  = formatChartYAxisLabel(value, yAxisDecimals);

                this.chartGroup.add(new Konva.Line({
                    points: [leftPad - yTickLen, y, leftPad, y],
                    stroke: yTickColor,
                    strokeWidth: yTickWidth,
                    listening: false,
                }));

                const labelWidth = Math.ceil(text.length * yLabelFontSize * 0.6);
                this.chartGroup.add(new Konva.Text({
                    x: leftPad - yTickLen - yLabelGap - labelWidth,
                    y: y - yLabelFontSize / 2,
                    text,
                    fontSize: yLabelFontSize,
                    fontFamily: yFontFamily,
                    fill: yTextColor,
                    listening: false,
                }));
            }

            // Minor ticks — subdivide each major-to-major gap into
            // yMinorTickCount equal intervals; never labeled (see
            // AXIS_STYLE's minor_tick_count comment for the exact semantics).
            if (yMinorTickCount > 1) {
                const yMajorStep = ch / (hdiv + 1);
                for (let i = 0; i <= hdiv; i++) {
                    const yGapStart = yMajorStep * i;
                    for (let k = 1; k < yMinorTickCount; k++) {
                        const y = yGapStart + (yMajorStep * k) / yMinorTickCount;
                        this.chartGroup.add(new Konva.Line({
                            points: [leftPad - yMinorTickSize, y, leftPad, y],
                            stroke: yMinorTickColor,
                            strokeWidth: yMinorTickWidth,
                            listening: false,
                        }));
                    }
                }
            }
        }

        // X-axis scale: tick marks + HA-style mixed labels below the chart area
        if (xScaleActive) {
            const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
            // Preview: last 24 h from now so labels look realistic
            const nowMs  = Date.now();
            const spanMs = 24 * 3600 * 1000;
            const nTicks = vdiv + 2; // left edge + vdiv inner lines + right edge
            let prevDay  = -1;

            for (let i = 0; i < nTicks; i++) {
                const x = leftPad + (cw / (nTicks - 1)) * i;
                const t = new Date(nowMs - spanMs + (spanMs / (nTicks - 1)) * i);

                // HA-style: new day → "DD mon", same day → "H:MM" (no leading zero)
                let text;
                if (t.getDate() !== prevDay) {
                    text = `${t.getDate()} ${MONTHS[t.getMonth()]}`;
                } else {
                    const mm = String(t.getMinutes()).padStart(2, '0');
                    text = `${t.getHours()}:${mm}`;
                }
                prevDay = t.getDate();

                // Tick mark downward from the bottom of the series area
                this.chartGroup.add(new Konva.Line({
                    points: [x, ch, x, ch + xTickLen],
                    stroke: xTickColor,
                    strokeWidth: xTickWidth,
                    listening: false,
                }));

                // Label centred below the tick
                const labelWidth = Math.ceil(text.length * xLabelFontSize * 0.6);
                this.chartGroup.add(new Konva.Text({
                    x: x - labelWidth / 2,
                    y: ch + xTickLen + xLabelGap,
                    text,
                    fontSize: xLabelFontSize,
                    fontFamily: xFontFamily,
                    fill: xTextColor,
                    listening: false,
                }));
            }

            // Minor ticks — subdivide each major-to-major gap into
            // xMinorTickCount equal intervals; never labeled.
            if (xMinorTickCount > 1) {
                const xMajorStep = cw / (nTicks - 1);
                for (let i = 0; i < nTicks - 1; i++) {
                    const xGapStart = leftPad + xMajorStep * i;
                    for (let k = 1; k < xMinorTickCount; k++) {
                        const x = xGapStart + (xMajorStep * k) / xMinorTickCount;
                        this.chartGroup.add(new Konva.Line({
                            points: [x, ch, x, ch + xMinorTickSize],
                            stroke: xMinorTickColor,
                            strokeWidth: xMinorTickWidth,
                            listening: false,
                        }));
                    }
                }
            }
        }

        const series     = Array.isArray(this.series) ? this.series : [];
        const pointCount = Math.max(2, Number(this.point_count) || 10);

        series.forEach((color, idx) => {
            const pts = generateSeriesSample(pointCount, idx, minVal, range);
            const n   = pts.length;

            if (chartType === 2) {
                // Column / bar chart
                const barW = Math.max(1, (cw / n) * 0.6);
                pts.forEach((val, i) => {
                    const norm = Math.max(0, Math.min(1, (val - minVal) / range));
                    const barH = norm * ch;
                    const bx   = leftPad + (cw / n) * i + (cw / n - barW) / 2;
                    const by   = ch - barH;
                    this.chartGroup.add(new Konva.Rect({
                        x: bx, y: by, width: barW, height: barH,
                        fill: color,
                        listening: false,
                    }));
                });
            } else if (chartType === 1) {
                // Line chart
                const points = [];
                pts.forEach((val, i) => {
                    const norm = Math.max(0, Math.min(1, (val - minVal) / range));
                    points.push(leftPad + (cw / (n - 1)) * i, ch - norm * ch);
                });
                this.chartGroup.add(new Konva.Line({
                    points,
                    stroke: color,
                    strokeWidth: lineWidth,
                    lineCap: 'round',
                    lineJoin: 'round',
                    listening: false,
                }));
                if (pointSize > 0) {
                    for (let i = 0; i < points.length; i += 2) {
                        this.chartGroup.add(new Konva.Circle({
                            x: points[i], y: points[i + 1],
                            radius: pointSize,
                            fill: color,
                            listening: false,
                        }));
                    }
                }
            }
        });

        this.add(this.chartGroup);
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);
        if (Array.isArray(this.series)) {
            objectData.series = this.series;
        }
        if (this.dataset) {
            objectData.dataset = this.dataset;
        }
        // Keep explicitly configured sides (and sides expanded from an
        // explicitly configured overall value), while dropping values that
        // only came from schema/theme defaults.
        const overallPadding = this.resolveStyleTrace('main', 'padding', 'default');
        for (const [side, wireKey] of [
            ['pad_top', 'pad_top'], ['pad_right', 'pad_right'],
            ['pad_bottom', 'pad_bottom'], ['pad_left', 'pad_left'],
        ]) {
            const sidePadding = this.resolveStyleTrace('main', side, 'default');
            if (!shouldExportChartPaddingSide(overallPadding, sidePadding)) delete objectData[wireKey];
        }
    }
}
